// src/App.jsx - Apartment Management System (Updated)
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import i18n from './i18n';

// Context Providers
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './admin/components/PublicRoute';
import RequireSubscription from './components/RequireSubscription';

// Pages - Auth
import Login from './pages/Login';
import Register from './pages/Register';

// Admin Layout
import AdminLayout from './admin/AdminLayout';

// Admin Pages - Dashboard
import Dashboard from './admin/pages/Dashboard';

// Admin Pages - Users
import UserList from './admin/components/users/UserList';
import UserForm from './admin/components/users/UserForm';

// Admin Pages - Buildings
import BuildingList from './admin/components/buildings/BuildingList';
import BuildingForm from './admin/components/buildings/BuildingForm';
import BuildingView from './admin/components/buildings/BuildingView';

// Admin Pages - Apartments (renamed from Properties)
import ApartmentList from './admin/components/apartments/ApartmentList';
import ApartmentForm from './admin/components/apartments/ApartmentForm';
import ApartmentDetailView from './admin/components/apartments/ApartmentDetailView';

// Admin Pages - Owners
import OwnerList from './admin/components/owners/OwnerList';
import OwnerForm from './admin/components/owners/OwnerForm';
import OwnerDetailView from './admin/components/owners/OwnerDetailView';

// Admin Pages - Sales
import SaleList from './admin/components/sales/SaleList';
import SaleForm from './admin/components/sales/SaleForm';
import SaleDetailView from './admin/components/sales/SaleDetailView';

// Admin Pages - Tenants
import TenantList from './admin/components/tenants/TenantList';
import TenantForm from './admin/components/tenants/TenantForm';
import TenantDetailView from './admin/components/tenants/TenantDetailView';

// Admin Pages - Payments
import PaymentList from './admin/components/payments/PaymentList';
import PaymentForm from './admin/components/payments/PaymentForm';
import PaymentDetailView from './admin/components/payments/PaymentDetailView';
import SubscriptionPaymentList from './admin/components/payments/SubscriptionPaymentList';

// Admin Pages - Reports
import ReportDashboard from './admin/components/reports/ReportDashboard';
import RevenueReport from './admin/components/reports/RevenueReport';
import PaymentLogsReport from './admin/components/reports/PaymentLogsReport';
import OccupancyReport from './admin/components/reports/OccupancyReport';
import PrintReceipt from './admin/components/reports/PrintReceipt';
import MonthlyPaymentsReport from './admin/components/reports/MonthlyPaymentsReport';

// Admin Pages - Bookings
import BookingList from './admin/components/bookings/BookingList';
import BookingForm from './admin/components/bookings/BookingForm';
import BookingDetail from './admin/components/bookings/BookingDetail';
import BookingWaitlist from './admin/components/bookings/BookingWaitlist';
import BookingCalendar from './admin/components/bookings/BookingCalendar';
import BookingAnalytics from './admin/components/bookings/BookingAnalytics';

// Service Payments
import ServiceManagement from './admin/components/services/ServiceManagement';
import ServicePaymentGeneration from './admin/components/services/ServicePaymentGeneration';
import ServiceStatistics from './admin/components/services/ServiceStatistics';
import ServicePaymentDetail from './admin/components/services/ServicePaymentDetail';

import ServicePaymentList from './admin/components/services/ServicePaymentList';

// Logs
import ActivityLogs from './admin/components/logs/ActivityLogs';
import TenantLogs from './admin/components/logs/TenantLogs';
import MonthlyRevenueByUser from './admin/components/logs/MonthlyRevenueByUser';

// WhatsApp Settings
import WhatsAppSettings from './admin/components/whatsapp/WhatsAppSettings';

// Admin Pages - Settings
import Settings from './admin/components/settings/Settings';
import ProfileSettings from './admin/components/settings/ProfileSettings';
import SystemSettings from './admin/components/settings/SystemSettings';

// Error Pages
import NotFound from './components/NotFound';
import ErrorBoundary from './components/ErrorBoundary';

// Global Styles
import './App.css';

function App() {
  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <AuthProvider>
            <Router>
              <Toaster position="top-right" />
              <div className="App">
                <Routes>
                  {/* Auth Routes */}
                  <Route path="/" element={<Navigate to="/login" replace />} />

                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <Login />
                      </PublicRoute>
                    }
                  />

                  <Route
                    path="/register"
                    element={
                      <PublicRoute>
                        <Register />
                      </PublicRoute>
                    }
                  />

                  {/* Admin Routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute>
                        <RequireSubscription>
                          <AdminLayout />
                        </RequireSubscription>
                      </ProtectedRoute>
                    }
                  >
                    {/* Dashboard */}
                    <Route path="dashboard" element={<Dashboard />} />

                    {/* User Management Routes (Admin Only) */}
                    <Route path="users/*" element={
                      <ProtectedRoute requiredRole="admin">
                        <Routes>
                          <Route path="/" element={<UserList />} />
                          <Route path="add" element={<UserForm />} />
                          <Route path=":id/edit" element={<UserForm isEdit={true} />} />
                        </Routes>
                      </ProtectedRoute>
                    } />

                    {/* Building Management Routes */}
                    <Route path="buildings" element={<BuildingList />} />
                    <Route path="buildings/add" element={<BuildingForm />} />
                    <Route path="buildings/:id" element={<BuildingView />} />
                    <Route path="buildings/:id/edit" element={<BuildingForm isEdit={true} />} />

                    {/* Apartment Management Routes (renamed from Properties) */}
                    <Route path="apartments" element={<ApartmentList />} />
                    <Route path="apartments/add" element={<ApartmentForm />} />
                    <Route path="apartments/:id" element={<ApartmentDetailView />} />
                    <Route path="apartments/:id/edit" element={<ApartmentForm isEdit={true} />} />

                    {/* Owner Management Routes */}
                    <Route path="owners" element={<OwnerList />} />
                    <Route path="owners/add" element={<OwnerForm />} />
                    <Route path="owners/:id" element={<OwnerDetailView />} />
                    <Route path="owners/:id/edit" element={<OwnerForm isEdit={true} />} />

                    {/* Sales Routes */}
                    <Route path="sales" element={<SaleList />} />
                    <Route path="sales/add" element={<SaleForm />} />
                    <Route path="sales/:id" element={<SaleDetailView />} />

                    {/* Tenant Management Routes */}
                    <Route path="tenants" element={<TenantList />} />
                    <Route path="tenants/add" element={<TenantForm />} />
                    <Route path="tenants/:id" element={<TenantDetailView />} />
                    <Route path="tenants/:id/edit" element={<TenantForm isEdit={true} />} />
                    <Route path="tenants/overdue" element={<TenantList filter="overdue" />} />

                    {/* Booking Management Routes */}
                    <Route path="bookings" element={<BookingList />} />
                    <Route path="bookings/add" element={<BookingForm />} />
                    <Route path="bookings/calendar" element={<BookingCalendar />} />
                    <Route path="bookings/waitlist" element={<BookingWaitlist />} />
                    <Route path="bookings/analytics" element={<BookingAnalytics />} />
                    <Route path="bookings/:id" element={<BookingDetail />} />
                    <Route path="bookings/:id/edit" element={<BookingForm />} />

                    {/* Payment Management Routes */}
                    <Route path="payments" element={<PaymentList />} />
                    <Route path="payments/add" element={<PaymentForm />} />
                    <Route path="payments/:id" element={<PaymentDetailView />} />
                    <Route path="payments/:id/edit" element={<PaymentForm isEdit={true} />} />
                    <Route path="payments/:id/edit" element={<PaymentForm isEdit={true} />} />
                    <Route path="payments/stats" element={<PaymentDetailView view="stats" />} />

                    {/* Settings Routes */}
                    <Route path="settings/subscription" element={<SubscriptionPaymentList />} />

                    {/* Reports Routes (Admin only) */}
                    <Route path="reports/*" element={
                      <ProtectedRoute requiredRole="admin">
                        <Routes>
                          <Route path="/" element={<ReportDashboard />} />
                          <Route path="revenue" element={<RevenueReport />} />
                          <Route path="payment-logs" element={<PaymentLogsReport />} />
                          <Route path="occupancy" element={<OccupancyReport />} />
                          <Route path="print-receipt/:paymentId" element={<PrintReceipt />} />
                          <Route path="tenant-payments" element={<TenantList filter="payment-status" />} />
                          <Route path="building-revenue" element={<RevenueReport type="building" />} />
                          <Route path="owner-revenue" element={<RevenueReport type="owner" />} />
                          <Route path="monthly-revenue/:year" element={<RevenueReport type="monthly" />} />
                          <Route path="monthly-payments" element={<MonthlyPaymentsReport />} />
                        </Routes>
                      </ProtectedRoute>
                    } />

                    {/* Logs Routes (Admin only) */}
                    <Route path="logs/*" element={
                      <ProtectedRoute requiredRole="admin">
                        <Routes>
                          <Route path="/" element={<ActivityLogs />} />
                          <Route path="tenants" element={<TenantLogs />} />
                          <Route path="monthly-revenue" element={<MonthlyRevenueByUser />} />
                        </Routes>
                      </ProtectedRoute>
                    } />

                    {/* Service Payment Management Routes */}
                    <Route path="services" element={<ServiceManagement />} />
                    <Route path="services/all" element={<ServicePaymentList />} />
                    <Route path="services/management" element={<ServiceManagement />} />
                    <Route path="services/building/:buildingId" element={<ServiceManagement />} />
                    <Route path="services/generate" element={<ServicePaymentGeneration />} />
                    <Route path="services/stats" element={<ServiceStatistics />} />
                    <Route path="services/:id" element={<ServicePaymentDetail />} />

                    {/* Settings Routes */}
                    <Route path="settings" element={<Settings />}>
                      <Route index element={<Navigate to="profile" replace />} />
                      <Route path="profile" element={<ProfileSettings />} />
                      <Route path="system" element={<SystemSettings />} />
                      <Route path="whatsapp" element={<WhatsAppSettings />} />
                    </Route>

                    {/* Default redirect */}

                    {/* Default redirect */}
                    <Route path="" element={<Navigate to="dashboard" replace />} />
                  </Route>


                  {/* 404 Page */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </Router>
          </AuthProvider>
        </ThemeProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;